import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Label } from 'recharts';
import { format, startOfMonth, endOfMonth } from 'date-fns';

interface DashboardStats {
  stats: {
    activeUsers: number;
    activeProjects: number;
    hoursThisWeek: number;
    hoursThisMonth: number;
    pendingTasks: number;
    completedTasksThisWeek: number;
  };
  recentActivity: Array<{
    date: string;
    hours: number;
    projectName: string;
    userName: string;
  }>;
}

interface Alert {
  type: string;
  severity: 'warning' | 'error' | 'info';
  title: string;
  message: string;
}

interface AlertsResponse {
  alerts: Alert[];
  summary: {
    total: number;
    warnings: number;
    errors: number;
  };
}

interface HoursData {
  groupBy: string;
  data: Array<{
    id: string;
    name: string;
    color?: string;
    totalHours: number;
    budgetHours?: number | null;
    budgetPercent?: number | null;
  }>;
}

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [alerts, setAlerts] = useState<AlertsResponse | null>(null);
  const [hoursData, setHoursData] = useState<HoursData | null>(null);
  const [allTimeHours, setAllTimeHours] = useState<HoursData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsData, alertsData, hoursResult, allTimeResult] = await Promise.all([
          api.get<DashboardStats>('/admin/dashboard'),
          api.get<AlertsResponse>('/admin/alerts'),
          api.get<HoursData>('/admin/hours', {
            groupBy: 'project',
            startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
            endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
          }),
          api.get<HoursData>('/admin/hours', { groupBy: 'project' }),
        ]);
        setStats(statsData);
        setAlerts(alertsData);
        setHoursData(hoursResult);
        setAllTimeHours(allTimeResult);
      } catch (err) {
        console.error('Failed to load admin dashboard:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const chartData = hoursData?.data.map((item, index) => ({
    ...item,
    fill: item.color || CHART_COLORS[index % CHART_COLORS.length],
  })) || [];

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
        <p className="text-dark-400 mt-1">Overview of team performance and alerts</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="card text-center">
          <p className="text-2xl font-bold text-white">{stats?.stats.activeUsers || 0}</p>
          <p className="text-sm text-dark-400">Active Users</p>
        </div>
        <div className="card text-center">
          <p className="text-2xl font-bold text-white">{stats?.stats.activeProjects || 0}</p>
          <p className="text-sm text-dark-400">Active Projects</p>
        </div>
        <div className="card text-center">
          <p className="text-2xl font-bold text-primary-400">{stats?.stats.hoursThisWeek || 0}h</p>
          <p className="text-sm text-dark-400">Hours This Week</p>
        </div>
        <div className="card text-center">
          <p className="text-2xl font-bold text-accent-400">{stats?.stats.hoursThisMonth || 0}h</p>
          <p className="text-sm text-dark-400">Hours This Month</p>
        </div>
        <div className="card text-center">
          <p className="text-2xl font-bold text-amber-400">{stats?.stats.pendingTasks || 0}</p>
          <p className="text-sm text-dark-400">Pending Tasks</p>
        </div>
        <div className="card text-center">
          <p className="text-2xl font-bold text-accent-400">{stats?.stats.completedTasksThisWeek || 0}</p>
          <p className="text-sm text-dark-400">Completed This Week</p>
        </div>
      </div>

      {/* Alerts */}
      {alerts && alerts.alerts.length > 0 && (
        <div className="card border-amber-500/30">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Alerts ({alerts.summary.total})
            </h2>
          </div>
          <div className="space-y-3">
            {alerts.alerts.slice(0, 5).map((alert, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg ${
                  alert.severity === 'error' ? 'bg-red-500/10 border border-red-500/20' :
                  alert.severity === 'warning' ? 'bg-amber-500/10 border border-amber-500/20' :
                  'bg-primary-500/10 border border-primary-500/20'
                }`}
              >
                <p className={`font-medium ${
                  alert.severity === 'error' ? 'text-red-400' :
                  alert.severity === 'warning' ? 'text-amber-400' :
                  'text-primary-400'
                }`}>
                  {alert.title}
                </p>
                <p className="text-sm text-dark-400 mt-1">{alert.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        {/* Hours distribution bar chart */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Hours Distribution <span className="text-sm font-normal text-dark-400">({format(new Date(), 'MMMM yyyy')})</span></h2>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 40 + 40)}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20, top: 5, bottom: 25 }}>
                <XAxis type="number" stroke="#64748b" fontSize={12}>
                  <Label value="Hours" position="bottom" offset={5} fill="#94a3b8" fontSize={12} />
                </XAxis>
                <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={12} width={160} tick={{ fill: '#94a3b8' }}>
                  <Label value="Project" angle={-90} position="insideLeft" offset={-5} fill="#94a3b8" fontSize={12} />
                </YAxis>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [`${value}h`, 'Hours']}
                />
                <Bar dataKey="totalHours" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-dark-500">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* All-time hours by project table */}
      {allTimeHours && allTimeHours.data.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Project Hours <span className="text-sm font-normal text-dark-400">(All Time)</span></h2>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th className="text-right">Total Hours</th>
                  <th className="text-right">Budget</th>
                  <th className="text-right">% Used</th>
                </tr>
              </thead>
              <tbody>
                {allTimeHours.data
                  .filter(p => p.totalHours > 0)
                  .map((project) => (
                  <tr key={project.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color || '#6B7280' }} />
                        <span className="font-medium text-dark-200">{project.name}</span>
                      </div>
                    </td>
                    <td className="text-right text-dark-200 font-medium">{project.totalHours}h</td>
                    <td className="text-right text-dark-400">
                      {project.budgetHours ? `${project.budgetHours}h` : '—'}
                    </td>
                    <td className="text-right">
                      {project.budgetPercent != null ? (
                        <span className={`font-medium ${
                          project.budgetPercent > 100 ? 'text-red-400' :
                          project.budgetPercent > 80 ? 'text-amber-400' :
                          'text-accent-400'
                        }`}>
                          {project.budgetPercent}%
                        </span>
                      ) : (
                        <span className="text-dark-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-dark-700">
                  <td className="font-medium text-dark-200">Total</td>
                  <td className="text-right font-bold text-white">
                    {allTimeHours.data.reduce((sum, p) => sum + p.totalHours, 0)}h
                  </td>
                  <td className="text-right text-dark-400">
                    {allTimeHours.data.reduce((sum, p) => sum + (p.budgetHours || 0), 0) > 0
                      ? `${allTimeHours.data.reduce((sum, p) => sum + (p.budgetHours || 0), 0)}h`
                      : '—'}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Recent activity */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Recent Activity</h2>
          <Link to="/admin/reports" className="text-sm text-primary-400 hover:text-primary-300">
            View all
          </Link>
        </div>
        {stats?.recentActivity && stats.recentActivity.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Project</th>
                  <th>Date</th>
                  <th className="text-right">Hours</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentActivity.map((activity, index) => (
                  <tr key={index}>
                    <td className="text-dark-200">{activity.userName}</td>
                    <td className="text-dark-400">{activity.projectName}</td>
                    <td className="text-dark-400">{activity.date}</td>
                    <td className="text-right font-medium text-white">{activity.hours}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-dark-500">
            No recent activity
          </div>
        )}
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/admin/users" className="card-hover flex items-center gap-4">
          <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-white">Manage Users</h3>
            <p className="text-sm text-dark-400">Add, edit, and manage team members</p>
          </div>
        </Link>

        <Link to="/admin/projects" className="card-hover flex items-center gap-4">
          <div className="w-12 h-12 bg-accent-500/20 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-accent-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-white">Manage Projects</h3>
            <p className="text-sm text-dark-400">Create and organize projects</p>
          </div>
        </Link>

        <Link to="/admin/reports" className="card-hover flex items-center gap-4">
          <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-white">View Reports</h3>
            <p className="text-sm text-dark-400">Detailed analytics and exports</p>
          </div>
        </Link>
      </div>
    </div>
  );
}
